import {
  tsvClipboardPayloadSerializer,
  tsvClipboardSourceAdapter,
} from 'helpers/tableClipboard.helper/tableClipboard.helper'
import type { RefObject } from 'react'
import { useEffect, useLayoutEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import type { CellCoordinate, RowValues, TableCellAdapter } from '../HourlyDataTable.types'

type UseTableCellCopyOptions = {
  tableContainerRef: RefObject<HTMLDivElement | null>
  adapter: TableCellAdapter
  adapterRef?: React.MutableRefObject<TableCellAdapter>
  selectedCellKeysForClipboardRef: React.MutableRefObject<Set<string>>
  valuesByRowRef: React.MutableRefObject<RowValues[]>
}

type UseTableCellClipboardOptions = UseTableCellCopyOptions & {
  anchorCellKeyForClipboardRef: React.MutableRefObject<string | null>
  setValuesByRow: React.Dispatch<React.SetStateAction<RowValues[]>>
  isCellEditable?: (cellKey: string) => boolean
}

function copyTableCells(
  event: ClipboardEvent,
  selectedCellKeysRef: React.MutableRefObject<Set<string>>,
  valuesByRowRef: React.MutableRefObject<RowValues[]>,
  adapterRef: React.MutableRefObject<TableCellAdapter>,
  t: (key: string) => string
): void {
  const selectedKeys = selectedCellKeysRef.current
  if (selectedKeys.size === 0) {
    return
  }

  const cellMap = new Map<string, string>()
  let minRow = Infinity
  let maxRow = -Infinity
  let minCol = Infinity
  let maxCol = -Infinity

  selectedKeys.forEach((cellKey) => {
    const coord = adapterRef.current.getCoordinate(cellKey)
    if (!coord) {
      return
    }
    const value = valuesByRowRef.current[coord.rowIndex]?.[cellKey] ?? ''
    cellMap.set(`${coord.rowIndex},${coord.columnIndex}`, value)
    if (coord.rowIndex < minRow) minRow = coord.rowIndex
    if (coord.rowIndex > maxRow) maxRow = coord.rowIndex
    if (coord.columnIndex < minCol) minCol = coord.columnIndex
    if (coord.columnIndex > maxCol) maxCol = coord.columnIndex
  })

  if (cellMap.size === 0) {
    return
  }

  const matrix: string[][] = []
  for (let row = minRow; row <= maxRow; row++) {
    const matrixRow: string[] = []
    for (let col = minCol; col <= maxCol; col++) {
      matrixRow.push(cellMap.get(`${row},${col}`) ?? '')
    }
    matrix.push(matrixRow)
  }

  const tsvText = tsvClipboardPayloadSerializer.fromClipboardPayload(matrix)

  if (event.clipboardData) {
    event.clipboardData.setData('text/plain', tsvText)
  } else {
    navigator.clipboard.writeText(tsvText).catch(() => {
      alertStore.error(t('Failed to copy to clipboard'))
    })
  }
  event.preventDefault()
}

function pasteTableCells(
  event: ClipboardEvent,
  anchorCellKeyRef: React.MutableRefObject<string | null>,
  adapterRef: React.MutableRefObject<TableCellAdapter>,
  isCellEditable: (cellKey: string) => boolean,
  setValuesByRow: React.Dispatch<React.SetStateAction<RowValues[]>>
): void {
  const clipboardText = event.clipboardData?.getData('text/plain')
  const anchorKey = anchorCellKeyRef.current

  if (!clipboardText || !anchorKey) {
    return
  }

  const anchorCoord = adapterRef.current.getCoordinate(anchorKey)
  if (!anchorCoord) {
    return
  }

  const matrix = tsvClipboardSourceAdapter.toClipboardPayload(clipboardText)
  const currentAdapter = adapterRef.current
  const assignments: Array<{ rowIndex: number; cellKey: string; rawValue: string }> = []

  for (let rowDelta = 0; rowDelta < matrix.length; rowDelta++) {
    const matrixRow = matrix[rowDelta]
    for (let colDelta = 0; colDelta < matrixRow.length; colDelta++) {
      const targetCoord: CellCoordinate = {
        rowIndex: anchorCoord.rowIndex + rowDelta,
        columnIndex: anchorCoord.columnIndex + colDelta,
      }
      if (targetCoord.rowIndex >= currentAdapter.rowCount || targetCoord.columnIndex >= currentAdapter.columnCount) {
        continue
      }
      const cellKey = currentAdapter.getCellKey(targetCoord)
      if (!cellKey || !isCellEditable(cellKey)) {
        continue
      }
      assignments.push({
        rowIndex: targetCoord.rowIndex,
        cellKey,
        rawValue: matrixRow[colDelta] ?? '',
      })
    }
  }

  if (assignments.length === 0) {
    return
  }

  event.preventDefault()
  setValuesByRow((previousValues) => {
    const next = [...previousValues]
    for (const { rowIndex, cellKey, rawValue } of assignments) {
      if (rowIndex < next.length) {
        next[rowIndex] = { ...(next[rowIndex] ?? {}), [cellKey]: rawValue }
      }
    }
    return next
  })
}

export function useTableCellCopy({
  tableContainerRef,
  adapter,
  adapterRef: sharedAdapterRef,
  selectedCellKeysForClipboardRef,
  valuesByRowRef,
}: UseTableCellCopyOptions): void {
  const { t } = useTranslation()

  const ownAdapterRef = useRef(adapter)
  const adapterRef = sharedAdapterRef ?? ownAdapterRef
  useLayoutEffect(() => {
    adapterRef.current = adapter
  })

  useEffect(() => {
    const tableContainer = tableContainerRef.current
    if (!tableContainer) {
      return
    }

    const handleCopy = (event: ClipboardEvent): void =>
      copyTableCells(event, selectedCellKeysForClipboardRef, valuesByRowRef, adapterRef, t)
    tableContainer.addEventListener('copy', handleCopy as EventListener)
    return () => {
      tableContainer.removeEventListener('copy', handleCopy as EventListener)
    }
  }, [adapterRef, selectedCellKeysForClipboardRef, t, tableContainerRef, valuesByRowRef])
}

export function useTableCellClipboard({
  tableContainerRef,
  adapter,
  anchorCellKeyForClipboardRef,
  selectedCellKeysForClipboardRef,
  valuesByRowRef,
  setValuesByRow,
  isCellEditable = () => true,
}: UseTableCellClipboardOptions): void {
  const adapterRef = useRef(adapter)
  useTableCellCopy({
    tableContainerRef,
    adapter,
    adapterRef,
    selectedCellKeysForClipboardRef,
    valuesByRowRef,
  })

  useEffect(() => {
    const tableContainer = tableContainerRef.current
    if (!tableContainer) {
      return
    }

    const handlePaste = (event: ClipboardEvent): void =>
      pasteTableCells(event, anchorCellKeyForClipboardRef, adapterRef, isCellEditable, setValuesByRow)
    tableContainer.addEventListener('paste', handlePaste as EventListener)
    return () => {
      tableContainer.removeEventListener('paste', handlePaste as EventListener)
    }
  }, [anchorCellKeyForClipboardRef, isCellEditable, setValuesByRow, tableContainerRef])
}
